import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { CampaignData } from "../../types";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import useRequest from "../../hook/use-req";

interface GeneratedTemplate {
  id: number;
  title: string;
  subject: string;
  header: string;
  messages: string;
  plainText: string;
  html: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
}

type Intent = "promotional" | "welcome" | "newsletter" | "winback";
type Tone = "Professional" | "Friendly & Casual" | "Energetic & Fun" | "Luxury & Sophisticated" | "Custom Voice";
type PersonalizationLevel =
  | "High (Name + Custom Fields)"
  | "Medium (Name + Location)"
  | "Basic (Name Only)"
  | "No Personalization";

interface CampaignAiProps {
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
}

export const EmailContentCreation: React.FC<CampaignAiProps> = ({
  campaignData,
  setCampaignData,
  onSubmit,
  onValidation,
}) => {
  const [intent, setIntent] = useState<Intent>("promotional");
  const [tone, setTone] = useState<Tone | "">("");
  const [personalization, setPersonalization] = useState<PersonalizationLevel | "">("");
  const [emailGoal, setEmailGoal] = useState<string>("");
  const token = localStorage.getItem("authToken");

  const [emailSubject, setEmailSubject] = useState<string>(campaignData?.subject || "");
  const [emailHeader, setEmailHeader] = useState<string>(campaignData?.email_header || "");
  const [previewText, setPreviewText] = useState<string>(campaignData?.messages || "");
  const [htmlText, setHtmlText] = useState<string>(campaignData?.html_text || "");
  const [emailPlainText, setEmailPlainText] = useState<string>(campaignData?.plain_text || "");

  const [generatedTemplates, setGeneratedTemplates] = useState<GeneratedTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<GeneratedTemplate | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [contentSource, setContentSource] = useState<"generated" | "manual">("manual");

  const editorIframeRef = useRef<HTMLIFrameElement>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  const { makeRequest } = useRequest("/content/email", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      subject: campaignData?.subject || "",
      email_header: campaignData?.email_header || "",
      messages: campaignData?.messages || "",
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  // Convert HTML → Plain Text
  const convertHtmlToPlainText = useCallback((html: string): string => {
    if (!html) return "";
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<p\s*\/?>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n\s*\n/g, "\n\n")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .trim();
  }, []);

  // Keep campaignData in sync
  useEffect(() => {
    const plainText = emailPlainText || convertHtmlToPlainText(htmlText);
    setCampaignData((prev) => ({
      ...prev,
      ...watchedValues,
      subject: emailSubject,
      email_header: emailHeader,
      messages: previewText,
      html_text: htmlText,
      plain_text: plainText,
    }));
  }, [
    watchedValues,
    emailSubject,
    emailHeader,
    previewText,
    htmlText,
    emailPlainText,
    setCampaignData,
  ]);

  // Validation feedback
  useEffect(() => {
    const valid = isValid && emailSubject && htmlText;
    onValidation(!!valid);
  }, [isValid, emailSubject, htmlText, onValidation]);

  // Unlayer communication (same as before – simplified for brevity)
  const sendDataToUnlayer = useCallback((html: string) => {
    if (!editorIframeRef.current?.contentWindow) return false;
    const design = {
      counters: { u_column: 1, u_row: 1, u_content_html: 1 },
      body: {
        rows: [{
          cells: [1],
          columns: [{
            contents: [{
              type: "html",
              values: { html, containerPadding: "20px" }
            }],
            values: { backgroundColor: "", padding: "0px" }
          }],
          values: { padding: "0px" }
        }],
        values: { backgroundColor: "#ffffff", contentWidth: "600px" }
      },
      schemaVersion: 6
    };

    editorIframeRef.current.contentWindow!.postMessage(
      { type: "LOAD_DESIGN", data: { design } },
      "*"
    );
    return true;
  }, []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!["https://email.dalmlabs.com", "http://localhost:3000"].includes(e.origin)) return;

      if (e.data.type === "EDITOR_READY") {
        setIsEditorReady(true);
        setTimeout(() => {
          if (htmlText) sendDataToUnlayer(htmlText);
        }, 300);
      }
      if (e.data.type === "CONTENT_CHANGED" && e.data.data?.html) {
        setHtmlText(e.data.data.html);
        setEmailPlainText(convertHtmlToPlainText(e.data.data.html));
        setContentSource("manual");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [htmlText, sendDataToUnlayer, convertHtmlToPlainText]);

  // Generate Templates
  const generateTemplates = async () => {
    if (!tone || !personalization || !emailGoal) {
      toast.error("Please complete all AI settings");
      return;
    }
    setIsGenerating(true);
    setGeneratedTemplates([]);
    setSelectedTemplate(null);

    try {
      const payload = {
        brand_voice: tone,
        personalisation_level: personalization,
        message_goal: emailGoal,
        brand_name: intent,
      };
      const [res] = await makeRequest(payload);
      const templates: GeneratedTemplate[] = (res.messages || []).map((t: any, i: number) => {
        const colors = [
          { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", icon: "text-blue-600" },
          { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", icon: "text-emerald-600" },
          { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600" },
          { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", icon: "text-blue-600" },
        ][i % 4];

        return {
          id: i + 1,
          title: `Template ${i + 1}`,
          subject: t.subject || "Amazing Offer Inside!",
          header: t.header || "",
          messages: t.plain_text || "",
          plainText: t.plain_text || "",
          html: t.html || "<p>No content</p>",
          ...colors,
        };
      });

      setGeneratedTemplates(templates);
      toast.success(`${templates.length} templates generated!`);
      if (templates[0]) selectTemplate(templates[0]);
    } catch (err: any) {
      toast.error(err.message || "Failed to generate templates");
    } finally {
      setIsGenerating(false);
    }
  };

  const selectTemplate = (tmpl: GeneratedTemplate) => {
    setSelectedTemplate(tmpl);
    setEmailSubject(tmpl.subject);
    setEmailHeader(tmpl.header);
    setPreviewText(tmpl.messages);
    setHtmlText(tmpl.html);
    setEmailPlainText(tmpl.plainText);
    setContentSource("generated");

    setValue("subject", tmpl.subject);
    setValue("email_header", tmpl.header);
    setValue("messages", tmpl.messages);

    if (isEditorReady) sendDataToUnlayer(tmpl.html);
  };

  const handleFormSubmit = (data: any) => {
    const finalPlainText = contentSource === "generated" ? emailPlainText : convertHtmlToPlainText(htmlText);
    onSubmit({
      subject: emailSubject,
      email_header: emailHeader,
      messages: previewText,
      html_text: htmlText,
      plain_text: finalPlainText,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-6">
            {/* Campaign Settings */}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">AI Email Generator</h3>
                    <p className="text-xs text-gray-500">Intelligent email creation • Active</p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                  <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  Active
                </span>
              </div>

              <label className="block text-sm font-medium text-blue-600 border-l-4 p-1 border-blue-600 mb-2">
                Campaign Settings
              </label>

              {/* Intent */}
              <div className="lg:grid grid-cols-2 gap-2">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Intent</label>
                  <select
                    value={intent}
                    onChange={(e) => setIntent(e.target.value as Intent)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="promotional">Promotional</option>
                    <option value="welcome">Welcome</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="winback">Win-back</option>
                  </select>
                </div>

                {/* Tone */}
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value as Tone)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select tone...</option>
                    <option value="Professional">Professional</option>
                    <option value="Friendly & Casual">Friendly & Casual</option>
                    <option value="Energetic & Fun">Energetic & Fun</option>
                    <option value="Luxury & Sophisticated">Luxury & Sophisticated</option>
                  </select>
                </div>

                {/* Personalization */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Personalization</label>
                  <select
                    value={personalization}
                    onChange={(e) => setPersonalization(e.target.value as PersonalizationLevel)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select level...</option>
                    <option value="High (Name + Custom Fields)">High (Name + Custom Fields)</option>
                    <option value="Medium (Name + Location)">Medium (Name + Location)</option>
                    <option value="Basic (Name Only)">Basic (Name Only)</option>
                    <option value="No Personalization">No Personalization</option>
                  </select>
                </div>
              </div>

              {/* Email Goal */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-blue-600 border-l-4 p-1 border-blue-600 mb-2">
                  Email Description
                </label>
                <textarea
                  rows={4}
                  value={emailGoal}
                  onChange={(e) => setEmailGoal(e.target.value)}
                  placeholder="e.g. Create a promotional email for our new product launch with a modern design..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="button"
                onClick={generateTemplates}
                disabled={isGenerating || !tone || !personalization || !emailGoal}
                className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-600 text-white font-medium rounded-lg flex items-center justify-center gap-2 hover:from-blue-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate with AI
                  </>
                )}
              </button>
            </div>

            {/* Email Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-5">Email Details</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line *</label>
                  <Controller
                    name="subject"
                    control={control}
                    rules={{ required: "Subject is required" }}
                    render={({ field }) => (
                      <input
                        {...field}
                        value={emailSubject}
                        onChange={(e) => {
                          field.onChange(e);
                          setEmailSubject(e.target.value);
                        }}
                        placeholder="Enter email subject"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Header Text</label>
                  <input
                    value={emailHeader}
                    onChange={(e) => setEmailHeader(e.target.value)}
                    placeholder="Preview text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview Text</label>
                  <textarea
                    rows={3}
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    placeholder="This appears after the subject line in inbox"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Templates + Editor + Stats */}
          <div className="lg:col-span-8 space-y-6">
            {/* Generated Templates */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Email Preview</h3>
              {generatedTemplates.length === 0 ? (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                  <Sparkles className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">
                    {isGenerating ? "Generating your AI templates..." : "Enter a prompt and generate your email template"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {generatedTemplates.map((tmpl) => (
                    <div
                      key={tmpl.id}
                      onClick={() => selectTemplate(tmpl)}
                      className={`${tmpl.bgColor} ${tmpl.borderColor} border-2 rounded-lg p-5 cursor-pointer transition-all hover:shadow-md ${selectedTemplate?.id === tmpl.id ? "ring-4 ring-blue-400 ring-offset-2" : ""
                        }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4 className={`font-semibold ${tmpl.textColor}`}>{tmpl.title}</h4>
                        {selectedTemplate?.id === tmpl.id && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Selected</span>
                        )}
                      </div>
                      <p className="font-medium text-gray-800 text-sm mb-2 line-clamp-2">{tmpl.subject}</p>
                      <p className="text-xs text-gray-600 line-clamp-3">{tmpl.messages || "No preview text"}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email Editor */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-xl text-gray-800">Email Editor</h3>
                <div className="flex items-center gap-4 text-xs">
                  {/* <span className="text-gray-500">
                    {contentSource === "generated" ? "AI Generated" : "Manual Edit"}
                  </span> */}
                  <span className="font-medium text-blue-600">
                    {isEditorReady ? "Ready" : "Loading..."}
                  </span>
                </div>
              </div>
              <iframe
                ref={editorIframeRef}
                src={`https://email.dalmlabs.com?token=${encodeURIComponent(token || "")}`}
                className="w-full h-[800px] border-0"
                title="Email Editor"
              />
            </div>

            {/* Character & Size Stats */}
            <div className="rounded-xl border border-blue-200 shadow-md p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-5">Email Statistics</h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {/* Subject Line */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="text-xs font-medium text-gray-600">Subject Line</div>
                  <div className={`text-2xl font-bold mt-1 ${emailSubject.length > 70 ? "text-red-600" : emailSubject.length > 50 ? "text-amber-600" : "text-green-600"}`}>
                    {emailSubject.length}
                    <span className="text-sm font-normal text-gray-500 ml-1">/70</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {emailSubject.length === 0 && "Required"}
                    {emailSubject.length > 70 && "Too long"}
                    {emailSubject.length > 50 && emailSubject.length <= 70 && "Almost full"}
                    {emailSubject.length > 0 && emailSubject.length <= 50 && "Perfect"}
                  </div>
                </div>

                {/* Preview Text */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="text-xs font-medium text-gray-600">Preview Text</div>
                  <div className={`text-2xl font-bold mt-1 ${previewText.length > 140 ? "text-red-600" : "text-green-600"}`}>
                    {previewText.length}
                    <span className="text-sm font-normal text-gray-500 ml-1">/140</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {previewText.length > 140 ? "Will be truncated" : "Good length"}
                  </div>
                </div>

                {/* HTML Size */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="text-xs font-medium text-gray-600">HTML Size</div>
                  <div className={`text-2xl font-bold mt-1 ${htmlText.length > 100000 ? "text-red-600" : "text-blue-600"}`}>
                    {htmlText.length > 1024
                      ? (htmlText.length / 1024).toFixed(1) + " KB"
                      : htmlText.length + " B"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {htmlText.length > 100000 ? "Very large" : "Normal"}
                  </div>
                </div>

                {/* Total Size */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="text-xs font-medium text-gray-600">Total Size</div>
                  <div className={`text-2xl font-bold mt-1 ${(htmlText.length + emailPlainText.length) > 150000 ? "text-red-600" : "text-indigo-600"}`}>
                    {((htmlText.length + emailPlainText.length) / 1024).toFixed(1)} KB
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {htmlText.length + emailPlainText.length > 150000 ? "Risk of spam" : "Safe"}
                  </div>
                </div>
              </div>

              {/* Final Status */}
              <div className="mt-6 pt-5 border-t border-blue-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-700">Email Status</div>
                  <div className={`text-base font-bold mt-1 ${emailSubject && htmlText ? "text-green-600" : "text-red-600"}`}>
                    {emailSubject && htmlText ? "Ready to Send" : "Incomplete"}
                  </div>
                </div>
                {emailSubject && htmlText && (
                  <div className="text-lg">Ready</div>
                )}
                {!emailSubject && (
                  <div className="text-sm text-red-600">Subject missing</div>
                )}
                {!htmlText && (
                  <div className="text-sm text-red-600">Content missing</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

    </>
  );
};